import { get, isEmpty, isNil, noop } from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation } from "shared/hooks/useApi";
import graphql from "utils/api/graphql";
import { bytesOf } from "./pdf-reader";

const PREVIEW_DELAY = 600;

const usePreview = ({ template: { id }, mappings, payload, enabled }) => {
  const [previewDocument] = useMutation(
    graphql.mutations.PREVIEW_DOCUMENT_TEMPLATE,
    { onError: noop },
  );
  const [bytes, setBytes] = useState(null);
  const [unresolved, setUnresolved] = useState([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const sequence = useRef(0);
  const mappingsKey = useMemo(() => JSON.stringify(mappings || {}), [mappings]);

  useEffect(
    () => () => {
      sequence.current += 1;
    },
    [],
  );

  useEffect(() => {
    if (!enabled || isNil(payload)) return undefined;
    const timer = setTimeout(async () => {
      sequence.current += 1;
      const call = sequence.current;
      setLoading(true);
      const result = await previewDocument({
        variables: {
          where: { id },
          mappings: JSON.parse(mappingsKey),
          payload,
        },
      });
      if (call !== sequence.current) return;
      setLoading(false);
      const base64 = get(result, "data.previewDocumentTemplate.base64");
      if (isEmpty(base64)) {
        setFailed(true);
        return;
      }
      setBytes(bytesOf(base64));
      setFailed(false);
      setUnresolved(
        get(result, "data.previewDocumentTemplate.unresolved") || [],
      );
    }, PREVIEW_DELAY);
    return () => clearTimeout(timer);
  }, [id, mappingsKey, payload, enabled, previewDocument]);

  return { bytes, unresolved, loading, failed };
};

export default usePreview;
